// Filtros.tsx
import React from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  setPage,
  setTake,
  selectSearchCriteria,
  selectFechaNovedadDesde,
  selectFechaNovedadHasta,
  selectInicioVigenciaDesde,
  selectInicioVigenciaHasta,
  selectNegocio,
  selectCompania,
  selectTipoNovedad,
  descargarReporte,
} from "./actions";
import { calculateDate } from "~libs/dateCalculator";
import Searcher from "~styled/searcher/searcher";
import { Button, Tooltip, Typography } from "@mui/material";
import Pagination from "~styled/pagination/Pagination";
import DateRangePicker from "~styled/dateRangePicker/DateRangePicker";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { paginationOptions } from "~constants/pagination";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import { negociosFilter, negociosSegurosOptions } from "~constants/negocios";
import { companiasSegurosOptions, companiasFilter } from "~constants/companias";
import {
  tipoNovedadSegurosOptions,
  tiposNovedadesFilter,
} from "~constants/tipoNovedad";

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 20,
    width: "100%",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  searchers: {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: 20,
  },
  filters: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    lineHeight: "1.3",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingLeft: 5,
    paddingBottom: 5,
  },
  horizontalSpace: { paddingRight: "10px" },
  horizontalSpaceSearcher: { paddingRight: "10px", width: "100%" },
  searcherAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
  dateSelectorAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
  btnDescarga: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingLeft: "15px",
    marginLeft: "auto",
  },
}));

type V = string | number;

interface SliceNovedades {
  isLoading: boolean;
  totalRegistros: number;
  take: V;
  page: number;
  inicioVigenciaDesde: string | Date | null;
  inicioVigenciaHasta: string | Date | null;
  fechaNovedadDesde: string | Date | null;
  fechaNovedadHasta: string | Date | null;
  searchCriteria: string;
  negocio: V;
  compania: V;
  tipoNovedad: V;
  buttonDescargaEnabled: boolean;
  buttonDescargaText: string;
}

const useReduxSelector = () =>
  useSelector<any, SliceNovedades>((state) => ({
    isLoading: state.novedadesReducer.isLoading,
    totalRegistros: state.novedadesReducer.totalRegistros,

    take: state.filtrosNovedadesReducer.take,
    page: state.filtrosNovedadesReducer.page,
    inicioVigenciaDesde: state.filtrosNovedadesReducer.inicioVigenciaDesde,
    inicioVigenciaHasta: state.filtrosNovedadesReducer.inicioVigenciaHasta,
    fechaNovedadDesde: state.filtrosNovedadesReducer.fechaNovedadDesde,
    fechaNovedadHasta: state.filtrosNovedadesReducer.fechaNovedadHasta,
    searchCriteria: state.filtrosNovedadesReducer.searchCriteria,
    negocio: state.filtrosNovedadesReducer.negocio,
    compania: state.filtrosNovedadesReducer.compania,
    tipoNovedad: state.filtrosNovedadesReducer.tipoNovedad,
    buttonDescargaEnabled: state.filtrosNovedadesReducer.buttonDescargaEnabled,
    buttonDescargaText: state.filtrosNovedadesReducer.buttonDescargaText,
  }));

const messageLoading = "Cargando su consulta previa" as const;

const Filtros: React.FC = () => {
  const {
    isLoading,
    take,
    inicioVigenciaDesde,
    inicioVigenciaHasta,
    fechaNovedadDesde,
    fechaNovedadHasta,
    searchCriteria,
    negocio,
    compania,
    tipoNovedad,
    page,
    totalRegistros,
    buttonDescargaEnabled,
    buttonDescargaText,
  } = useReduxSelector();

  const dispatch = useDispatch();

  const handleChange = <T,>(
    event: { target: { value: T } },
    action: (v: T) => any
  ) => {
    dispatch(setPage(0));
    dispatch(action(event.target.value));
  };

  const handleChangeIntervalInicioDeVigencia = (value: number | null) => {
    dispatch(setPage(0));
    if (value == null) return;
    dispatch(selectInicioVigenciaDesde(calculateDate(new Date(), value)));
    dispatch(selectInicioVigenciaHasta(new Date()));
  };

  const handleChangeIntervalNovedadObtenida = (value: number | null) => {
    dispatch(setPage(0));
    if (value == null) return;
    dispatch(selectFechaNovedadDesde(calculateDate(new Date(), value)));
    dispatch(selectFechaNovedadHasta(new Date()));
  };

  const handleChangePage = (value: number) => {
    dispatch(setPage(value));
  };

  // El export exige el rango de novedad obtenida: sin él el backend rechaza el pedido.
  const rangoNovedadCompleto = Boolean(fechaNovedadDesde && fechaNovedadHasta);

  const handleDescargaReporte = () => {
    dispatch(
      descargarReporte({
        negocio,
        compania,
        tipoNovedad,
        searchCriteria,
        fechaNovedadDesde,
        fechaNovedadHasta,
        inicioVigenciaDesde,
        inicioVigenciaHasta,
      })
    );
  };

  const classes = useStyles();

  const negocioLabelMap = negociosSegurosOptions as Record<string, string>;
  const companiaLabelMap = companiasSegurosOptions as Record<string, string>;
  const tipoNovedadLabelMap = tipoNovedadSegurosOptions as Record<
    string,
    string
  >;

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((o) => ({
        label: String(o.label),
        value: o.value,
      })),
    []
  );

  return (
    <>
      <div className={classes.root}>
        <>
          <div className={classes.searchers}>
            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                NEGOCIO
              </Typography>

              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={negocioLabelMap[String(negocio)]}
                  options={negociosFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectNegocio)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                COMPAÑIA
              </Typography>

              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={companiaLabelMap[String(compania)]}
                  options={companiasFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectCompania)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                TIPO NOVEDAD
              </Typography>

              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={tipoNovedadLabelMap[String(tipoNovedad)]}
                  options={tiposNovedadesFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectTipoNovedad)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={classes.filters}>
              <div className={classes.dateSelectorAndTitle}>
                <Typography variant="h3" className={classes.label}>
                  INICIO DE VIGENCIA
                </Typography>
                <div className={classes.horizontalSpace}>
                  <DateRangePicker
                    fechaDesde={inicioVigenciaDesde}
                    fechaHasta={inicioVigenciaHasta}
                    onChangeInterval={handleChangeIntervalInicioDeVigencia}
                    onChangeStartDate={(value) =>
                      handleChange(
                        { target: { value } },
                        selectInicioVigenciaDesde
                      )
                    }
                    onChangeEndDate={(value) =>
                      handleChange(
                        { target: { value } },
                        selectInicioVigenciaHasta
                      )
                    }
                    onClickDisabled={() =>
                      dispatch(queueMessage(messageLoading))
                    }
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className={classes.btnDescarga}>
              <Tooltip
                title={
                  rangoNovedadCompleto
                    ? "Exportar el listado a un archivo Excel"
                    : "Cargá el rango de Novedad obtenida para exportar"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDescargaReporte}
                    disabled={
                      !buttonDescargaEnabled ||
                      isLoading ||
                      !rangoNovedadCompleto
                    }
                  >
                    {buttonDescargaText}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>

          <div className={classes.filtersContainer}>
            <div className={classes.filters}>
              <div className={classes.searcherAndTitle}>
                <Typography variant="h3" className={classes.label}>
                  BUSCAR
                </Typography>
                <div className={classes.horizontalSpaceSearcher}>
                  <Searcher
                    borderSquared
                    value={searchCriteria}
                    filter={searchCriteria}
                    placeholder="N° póliza, Dominio, Doc Asegurado"
                    onChange={(event) =>
                      handleChange(event, selectSearchCriteria)
                    }
                    onClear={() =>
                      handleChange(
                        { target: { value: "" } },
                        selectSearchCriteria
                      )
                    }
                  />
                </div>
              </div>

              <div className={classes.dateSelectorAndTitle}>
                <Typography variant="h3" className={classes.label}>
                  NOVEDAD OBTENIDA
                </Typography>
                <div className={classes.horizontalSpace}>
                  <DateRangePicker
                    fechaDesde={fechaNovedadDesde}
                    fechaHasta={fechaNovedadHasta}
                    onChangeInterval={handleChangeIntervalNovedadObtenida}
                    onChangeStartDate={(value) =>
                      handleChange(
                        { target: { value } },
                        selectFechaNovedadDesde
                      )
                    }
                    onChangeEndDate={(value) =>
                      handleChange(
                        { target: { value } },
                        selectFechaNovedadHasta
                      )
                    }
                    onClickDisabled={() =>
                      dispatch(queueMessage(messageLoading))
                    }
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {!isLoading && (
              <div className={classes.filters}>
                <Pagination<V>
                  rowsPerPage={take}
                  onChangeRowsPerPage={(value) =>
                    handleChange({ target: { value } }, setTake)
                  }
                  page={page}
                  onChangePage={handleChangePage}
                  totalRegistros={totalRegistros}
                  options={paginationOptionsStr as OptionKV<V>[]}
                />
              </div>
            )}
          </div>
        </>
      </div>
    </>
  );
};

export default Filtros;
